import React from "react";
import * as dayClient from "../clients/day-client";
import { statusMap, generateStatusObject } from "../utils/status";
import { makeMap } from "../utils/helpers";

const DayStateContext = React.createContext();
const DayDispatchContext = React.createContext();

/**
 * @const {string}
 */
const [FETCH_DAYS, CREATE_DAY, UPDATE_DAY, DELETE_DAY] = [
  "FETCH_DAYS",
  "CREATE_DAY",
  "UPDATE_DAY",
  "DELETE_DAY"
];

/**
 * @const {Object}
 */
const actionMap = {
  FETCH_DAYS,
  CREATE_DAY,
  UPDATE_DAY,
  DELETE_DAY
};

/**
 * Reducer function that takes in an action object, performs the action on the state, and returns the new state
 * @param {object} state - state object
 * @param {object} action - action object
 */
function DayReducer(state, action) {
  const { map, status } = state;
  const { type, payload, meta, error } = action;

  // Handle error
  if (error) {
    return {
      ...state,
      error: payload,
      status: {
        ...status,
        [type]: generateStatusObject(statusMap.ERROR)
      }
    };
  }

  // Dispatch has completed
  if (meta && meta.complete) {
    return {
      ...state,
      error: null,
      status: {
        ...status,
        [type]: generateStatusObject()
      }
    };
  }

  // No payload, so we are making a request
  if (!payload) {
    return {
      ...state,
      error: null,
      status: {
        ...status,
        [type]: generateStatusObject(statusMap.PENDING)
      }
    };
  }

  // Handle payloads for each action
  switch (type) {
    case FETCH_DAYS: {
      return {
        ...state,
        error: null,
        map: makeMap(payload),
        status: {
          ...status,
          [type]: generateStatusObject(statusMap.SUCCESS)
        }
      };
    }

    case CREATE_DAY: {
      return {
        ...state,
        error: null,
        map: { ...map, [payload._id]: payload },
        status: {
          ...status,
          [type]: generateStatusObject(statusMap.SUCCESS)
        }
      };
    }

    case UPDATE_DAY: {
      return {
        ...state,
        error: null,
        map: { ...map, [payload._id]: payload },
        status: {
          ...status,
          [type]: generateStatusObject(statusMap.SUCCESS)
        }
      };
    }

    case DELETE_DAY: {
      const mapCopy = { ...map };
      delete mapCopy[payload];
      return {
        ...state,
        error: null,
        map: mapCopy,
        status: {
          ...status,
          [type]: generateStatusObject(statusMap.SUCCESS)
        }
      };
    }

    default: {
      throw new Error(`Unhandled action type: ${type}`);
    }
  }
}

// Action creators
async function fetchDays(dispatch) {
  dispatch({ type: FETCH_DAYS });
  try {
    const days = await dayClient.fetchDays();
    dispatch({ type: FETCH_DAYS, payload: days });
  } catch (error) {
    dispatch({ type: FETCH_DAYS, payload: error, error: true });
  } finally {
    dispatch({ type: FETCH_DAYS, meta: { complete: true } });
  }
}

async function createDay(dispatch, day) {
  dispatch({ type: CREATE_DAY });
  try {
    const newDay = await dayClient.createDay(day);
    dispatch({ type: CREATE_DAY, payload: newDay[0] });
  } catch (error) {
    dispatch({ type: CREATE_DAY, payload: error, error: true });
  } finally {
    dispatch({ type: CREATE_DAY, meta: { complete: true } });
  }
}

async function updateDay(dispatch, dayId, updates) {
  dispatch({ type: UPDATE_DAY });
  try {
    const updatedDay = await dayClient.updateDay(dayId, updates);
    dispatch({ type: UPDATE_DAY, payload: updatedDay.value });
  } catch (error) {
    dispatch({ type: UPDATE_DAY, payload: error, error: true });
  } finally {
    dispatch({ type: UPDATE_DAY, meta: { complete: true } });
  }
}

async function deleteDay(dispatch, dayId) {
  dispatch({ type: DELETE_DAY });
  try {
    await dayClient.deleteDay(dayId);
    dispatch({ type: DELETE_DAY, payload: dayId });
  } catch (error) {
    dispatch({ type: DELETE_DAY, payload: error, error: true });
  } finally {
    dispatch({ type: DELETE_DAY, meta: { complete: true } });
  }
}

// Day Provider and consumer hooks
function DayProvider({ children }) {
  const initialStatus = generateStatusObject();

  const [state, dispatch] = React.useReducer(DayReducer, {
    map: {},
    status: {
      [FETCH_DAYS]: initialStatus,
      [CREATE_DAY]: initialStatus,
      [UPDATE_DAY]: initialStatus,
      [DELETE_DAY]: initialStatus
    }
  });

  return (
    <DayStateContext.Provider value={state}>
      <DayDispatchContext.Provider value={dispatch}>
        {children}
      </DayDispatchContext.Provider>
    </DayStateContext.Provider>
  );
}

function useDayState() {
  const context = React.useContext(DayStateContext);
  if (!context) {
    throw new Error(`useDayState must be used within a DayProvider`);
  }

  return context;
}

function useDayDispatch() {
  const context = React.useContext(DayDispatchContext);
  if (context === undefined) {
    throw new Error("useDayDispatch must be used within a DayProvider");
  }

  return context;
}

export {
  useDayState,
  useDayDispatch,
  DayProvider,
  actionMap,
  fetchDays,
  createDay,
  updateDay,
  deleteDay
};
